import React, { useState, useMemo, useCallback } from "react";
import {
  Plus,
  HelpCircle,
  Shield,
  Activity,
  Calendar,
  UserCheck,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { useSearchParams } from "react-router-dom";
import { useGetMasterActivityLogs } from "@features/wx/master/hooks";

// Activity Tab Component
const ActivityTab: React.FC<{ sopDetails: any }> = ({ sopDetails }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = 10;

  // Get current page from URL
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Fetch activity logs with pagination
  const { data: activityLogsResponse, isLoading } = useGetMasterActivityLogs(
    sopDetails.id,
    currentPage,
    itemsPerPage
  );

  // Extract data and pagination info
  const activityLogs = activityLogsResponse?.results || [];
  const totalItems = activityLogsResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Pagination info
  const pagination = useMemo(() => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [currentPage, totalItems, itemsPerPage]);

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const getActivityIcon = (title) => {
    if (title.includes("template review") || title.includes("publish")) {
      return { icon: Shield, color: "text-green-500 bg-green-50" };
    }
    if (title.includes("readings data") || title.includes("data update")) {
      return { icon: Activity, color: "text-blue-500 bg-blue-50" };
    }
    if (title.includes("materials") || title.includes("equipment")) {
      return { icon: Plus, color: "text-purple-500 bg-purple-50" };
    }
    return { icon: Activity, color: "text-gray-500 bg-gray-50" };
  };

  const formatActivityTitle = (title) => {
    if (title.includes("template review publish"))
      return "Template Review & Publish";
    if (title.includes("template readings data"))
      return "Template Readings Data Update";
    if (title.includes("template materials equipment"))
      return "Template Materials & Equipment Update";
    if (title.includes("template task definition"))
      return "Template Task Definition Update";
    if (title.includes("template safety requirements"))
      return "Template Safety Requirements Update";
    if (title.includes("template add")) return "Template Created";
    return "Activity Update";
  };

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between pb-2">
            <div className="flex items-center">
              <Activity className="mr-2 h-5 w-5 text-primary" />
              Activity Log
            </div>
            {pagination.totalItems > 0 && (
              <span className="text-sm text-gray-600">
                Showing {pagination.startItem}-{pagination.endItem} of{" "}
                {pagination.totalItems}
              </span>
            )}
          </CardTitle>
          <CardDescription>
            History of actions taken on this SOP template
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-gray-500">Loading activity logs...</div>
            </div>
          ) : activityLogs?.length > 0 ? (
            <>
              <div className="relative pl-8 space-y-8 before:absolute before:inset-y-0 before:left-4 before:w-px before:bg-slate-200">
                {activityLogs.map((log, index) => {
                  const { icon: ActivityIcon, color: iconColor } =
                    getActivityIcon(log.title);
                  const activityTitle = formatActivityTitle(log.title);

                  return (
                    <div
                      key={`${log.date}-${log.time}-${index}`}
                      className="relative"
                    >
                      <div
                        className={`absolute -left-8 mt-1.5 flex h-8 w-8 items-center justify-center rounded-full border border-white ${iconColor}`}
                      >
                        <ActivityIcon className="h-4 w-4" />
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900 capitalize">
                              {activityTitle}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {log.description}
                            </p>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {log.date} at {log.time}
                            </div>
                          </div>
                        </div>

                        {/* Updated by section */}
                        {log.user && (
                          <div className="mb-3 p-2 bg-gray-50 rounded border-l-4 border-blue-400">
                            <p className="text-xs text-gray-600">Updated by:</p>
                            <div className="flex items-center text-sm">
                              <UserCheck className="h-3 w-3 mr-1 text-blue-500" />
                              <span className="font-medium">
                                {log.user.name}
                              </span>
                              <span className="mx-2 text-gray-400">•</span>
                              <span className="text-gray-600">
                                {log.user.email}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {
                <div className="mt-6 flex items-center justify-between border-t pt-4">
                  <div className="text-sm text-gray-600">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }

                          return (
                            <Button
                              key={pageNumber}
                              variant={
                                currentPage === pageNumber
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() => handlePageChange(pageNumber)}
                              className="w-8 h-8 p-0"
                            >
                              {pageNumber}
                            </Button>
                          );
                        }
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              }
            </>
          ) : (
            <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium mb-2">No Activity Logs</h4>
              <p className="text-gray-600">
                No activity has been recorded for this SOP template yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityTab;
